import type { HTMLAttributes } from 'react';

import type { ECdnSettings, HiveToken, ViewerSourceMode } from '../../../../../generated/graphql-viewer';
import type { LsproPlayerConfig } from '../../use-player-config';

import {
  Player,
  type PlayerApi,
  type PlayerError,
  PlayerEvent,
  type TimedMetadataEvent,
} from '@movingimage-evp/player-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAutoplayUnmute } from './use-autoplay-unmute';
import { useStreamRetry } from './use-stream-retry';
import { useStreamSelection } from './use-stream-selection';
import { devLog } from '../../../../../utils';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import { usePlayerConfig } from '../../use-player-config';
import { UnmutePopup } from '../unmute-popup';
import { ErrorMessage } from './error-message';

export type ViewerPlayerProps = {
  webcastId: string;
  title?: string;
  accountId?: string;
  lsproId?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  muted?: boolean;
  autoplay?: boolean;
  hidePlaybackSpeed?: boolean;
  audioOnly?: boolean;
  employeeId?: string;
  primaryStreamReady?: boolean;
  backupStreamReady?: boolean;
  viewerSourceMode?: ViewerSourceMode;
  currentStream?: {
    playback: {
      primaryUrl: string;
      backupUrl?: string | null;
      token: string;
      ecdnSettings?: {
        hive?: {
          primary: HiveToken;
          backup?: HiveToken | null;
        } | null;
        vBrick?: ECdnSettings['vBrick'];
        kollective?: ECdnSettings['kollective'];
      } | null;
    };
  };
  onTimedMetadata?: (event: TimedMetadataEvent) => void;
  onOverlayVisibilityChange?: (visible: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function ViewerPlayer({
  webcastId,
  title,
  accountId,
  lsproId,
  primaryColor,
  iconColor,
  logoUrl,
  currentStream,
  muted = false,
  autoplay,
  hidePlaybackSpeed,
  audioOnly,
  employeeId,
  primaryStreamReady,
  backupStreamReady,
  viewerSourceMode,
  onTimedMetadata,
  onOverlayVisibilityChange,
  ...props
}: ViewerPlayerProps) {
  const { t } = useTranslation();
  const { selectedLanguage } = useSelectedLanguage();

  const { retryState, increaseRetryCount, resetRetryCount, cleanupTimers, isRetryScheduled } = useStreamRetry();
  const { streamToUse, handleStreamError, resetStreamSelection, isRetryStopped } = useStreamSelection({
    viewerSourceMode,
    primaryStreamReady,
    backupStreamReady,
    retryCount: retryState.count,
    resetRetryCount,
  });
  const { unmutePopupVisible, hideUnmutePopup, tryAutoplay, unmute } = useAutoplayUnmute();
  const [playerError, setPlayerError] = useState<{
    action: 'retry' | 'switch' | 'stop';
  } | null>(null);

  const primaryPlaybackUrl = currentStream?.playback.primaryUrl || '';
  const backupPlaybackUrl = currentStream?.playback.backupUrl || '';
  const playbackTokenRef = useRef(currentStream?.playback.token);

  const lastErrorTimeRef = useRef<number | null>(null);

  const playbackBaseUrl =
    streamToUse === 'primary' ? primaryPlaybackUrl : streamToUse === 'backup' ? backupPlaybackUrl : '';
  const token = playbackTokenRef.current ? `?hdnts=${playbackTokenRef.current}` : '';
  const retryParam = retryState.urlHash ? `${token ? '&' : '?'}retry=${retryState.urlHash}` : '';
  const playbackUrl = playbackBaseUrl + token + retryParam;

  const vBrickConfiguration = currentStream?.playback.ecdnSettings?.vBrick;
  const kollectiveConfiguration = currentStream?.playback.ecdnSettings?.kollective;
  const kollectiveConfigurationRef = useRef(kollectiveConfiguration);

  // Keep ref in sync during render so useMemo reads fresh values when it recomputes,
  // including clearing it when Kollective configuration is absent
  kollectiveConfigurationRef.current = kollectiveConfiguration;

  // Refresh playback token if the stream type changes
  useEffect(() => {
    if (currentStream?.playback.token) playbackTokenRef.current = currentStream.playback.token;
  }, [currentStream?.playback.token, streamToUse]);

  const primaryHiveConfig = currentStream?.playback.ecdnSettings?.hive?.primary;
  const backupHiveConfig = currentStream?.playback.ecdnSettings?.hive?.backup;
  const hiveConfiguration =
    streamToUse === 'primary' ? primaryHiveConfig : streamToUse === 'backup' ? backupHiveConfig : null;

  const lsproPlayerConfig = useMemo(
    (): LsproPlayerConfig => ({
      webcastId,
      contentId: webcastId,
      playbackUrl,
      title,
      accountId,
      lsproId,
      primaryColor,
      iconColor,
      logoUrl,
      muted,
      autoplay,
      hidePlaybackSpeed,
      audioOnly,
      hiveConfiguration,
      vBrickConfiguration,
      kollectiveConfiguration: kollectiveConfigurationRef.current,
      state: 'Live',
      language: selectedLanguage?.code,
      watermarkContent: employeeId,
    }),
    [
      accountId,
      autoplay,
      hidePlaybackSpeed,
      audioOnly,
      hiveConfiguration,
      logoUrl,
      lsproId,
      muted,
      playbackUrl,
      primaryColor,
      iconColor,
      selectedLanguage?.code,
      title,
      vBrickConfiguration,
      webcastId,
      employeeId,
    ]
  );

  const playerConfig = usePlayerConfig(lsproPlayerConfig);
  const [playerApi, setPlayerApi] = useState<PlayerApi | null>(null);

  const handleSourceSet = useCallback(() => {
    devLog('🔄 ViewerPlayer source set', streamToUse);
    if (isRetryStopped()) return;
    setPlayerError(null);
  }, [isRetryStopped, streamToUse]);

  const handlePlayerError = useCallback(
    (playerError: PlayerError) => {
      if (playerError?.message?.includes('interrupted by a call to pause')) {
        // Ignore the error triggered when autoplay is prevented by the browser
        return;
      }

      devLog('⚠️ ViewerPlayer error', playerError);
      hideUnmutePopup();

      // Prevent reacting to the same error repeatedly within a short time frame (e.g., 5 seconds)
      const now = Date.now();
      if (lastErrorTimeRef.current !== null && now - lastErrorTimeRef.current < 5000) {
        return;
      }

      lastErrorTimeRef.current = now;

      if (isRetryScheduled() || isRetryStopped()) {
        return;
      }

      const diagnostics = playerApi?.collectErrorDiagnostics();
      if (diagnostics) {
        devLog('⚠️ ViewerPlayer error details', diagnostics);
      }

      const action = handleStreamError(playerError.code, diagnostics, playerConfig?.visitorId);

      if (action === undefined) {
        return;
      }

      setPlayerError({ action });

      if (action === 'retry') {
        increaseRetryCount();
      } else if (action === 'switch') {
        lastErrorTimeRef.current = null;
      } else {
        cleanupTimers();
      }
    },
    [
      hideUnmutePopup,
      isRetryScheduled,
      isRetryStopped,
      playerApi,
      handleStreamError,
      playerConfig?.visitorId,
      increaseRetryCount,
      cleanupTimers,
    ]
  );

  const playerReadyHandler = useCallback((api: PlayerApi) => {
    devLog('✅ ViewerPlayer ready');
    setPlayerApi(api);
  }, []);

  // Add event listeners when player API or handlers change
  useEffect(() => {
    if (!playerApi) return;

    if (onTimedMetadata) {
      playerApi.addEventListener(PlayerEvent.TIMED_METADATA, onTimedMetadata);
    }
    playerApi.addEventListener(PlayerEvent.ERROR, handlePlayerError);
    playerApi.addEventListener(PlayerEvent.SOURCE_SET, handleSourceSet);

    return () => {
      if (onTimedMetadata) {
        playerApi.removeEventListener(PlayerEvent.TIMED_METADATA, onTimedMetadata);
      }
      playerApi.removeEventListener(PlayerEvent.ERROR, handlePlayerError);
      playerApi.removeEventListener(PlayerEvent.SOURCE_SET, handleSourceSet);
    };
  }, [playerApi, onTimedMetadata, handlePlayerError, handleSourceSet]);

  // Handle autoplay when player is ready
  useEffect(() => {
    if (playerApi && autoplay) {
      tryAutoplay(playerApi);
    }
  }, [playerApi, autoplay, tryAutoplay]);

  // Reset stream selection and error state when the language changes
  const previousLanguageRef = useRef(selectedLanguage?.code);
  useEffect(() => {
    if (previousLanguageRef.current !== selectedLanguage?.code) {
      previousLanguageRef.current = selectedLanguage?.code;
      setPlayerError(null);
      lastErrorTimeRef.current = null;
      cleanupTimers();
      resetStreamSelection();
    }
  }, [selectedLanguage?.code, cleanupTimers, resetStreamSelection]);

  useEffect(() => {
    if (playbackBaseUrl && playerConfig) devLog('⏯️ ViewerPlayer playerConfig', playerConfig, { memoize: true });
  }, [playbackBaseUrl, playerConfig]);

  if (!playbackBaseUrl || !playerConfig) return null;

  return (
    <div data-testid={streamToUse === 'primary' ? 'primary-stream-player' : 'backup-stream-player'} {...props}>
      <Player {...playerConfig} onReady={playerReadyHandler} onOverlayVisibilityChange={onOverlayVisibilityChange} />

      {unmutePopupVisible && (
        <UnmutePopup
          title={t('viewer.views.unmutePopup.title.eventStarted')}
          onClose={hideUnmutePopup}
          onUnMute={() => playerApi && unmute(playerApi)}
        />
      )}

      {playerError && <ErrorMessage action={playerError.action} />}
    </div>
  );
}
